import { Prompt, Variable } from '../../../domain/models/prompt';
import { StoragePrompt } from '../models/storageTypes';

type StorageVariable = {
  name: string;
  defaultValue?: string;
  type: 'text' | 'number' | 'selection' | 'date';
  options?: string[];
  required: boolean;
  description?: string;
};

export class PromptMapper {
  private static variablesToDomain(storageVars: StorageVariable[] | undefined): Variable[] {
    // Handle case where variables are undefined or null
    if (!storageVars) {
      console.log('MAPPER DEBUG: storageVars is undefined or null in variablesToDomain');
      return [];
    }

    return storageVars.map(v => ({
      name: v.name,
      defaultValue: v.defaultValue,
      type: v.type,
      options: v.options,
      required: v.required,
      description: v.description,
    }));
  }

  private static variablesToStorage(
    domainVars: readonly Variable[] | undefined
  ): StorageVariable[] {
    return (domainVars || []).map(v => ({
      name: v.name,
      defaultValue: v.defaultValue,
      type: v.type,
      options: v.options,
      required: v.required,
      description: v.description,
    }));
  }

  static toDomain(raw: StoragePrompt): Prompt {
    // Add defensive check for raw object
    if (!raw) {
      console.error('MAPPER DEBUG: raw StoragePrompt is null or undefined in toDomain');
      throw new Error('Cannot map null or undefined StoragePrompt to domain');
    }

    // Log the raw object for debugging
    console.log('MAPPER DEBUG: raw StoragePrompt in toDomain:', {
      id: raw.id,
      title: raw.title,
      hasVariables: !!raw.variables,
      variablesType: raw.variables ? typeof raw.variables : 'undefined',
    });

    return Prompt.fromProps({
      ...raw,
      variables: this.variablesToDomain(raw.variables),
    });
  }

  static toStorage(domain: Prompt): StoragePrompt {
    const obj = domain.toObject();
    return {
      ...obj,
      variables: this.variablesToStorage(obj.variables || []),
    };
  }

  static toDomainList(rawList: StoragePrompt[]): Prompt[] {
    return rawList.map(raw => this.toDomain(raw));
  }

  static toStorageList(domainList: Prompt[]): StoragePrompt[] {
    return domainList.map(domain => this.toStorage(domain));
  }
}
